import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  FileText,
  LifeBuoy,
  Mail,
  Send,
  Trash2,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { emitChange } from "@/lib/events";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useDelayedLoading } from "@/lib/useDelayedLoading";
import Layout from "@/components/Layout";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const STAGES = [
  { key: "open", title: "New", edge: "border-l-stock-out", text: "text-stock-out" },
  { key: "working", title: "Working on it", edge: "border-l-stock-low", text: "text-stock-low" },
  { key: "resolved", title: "Sorted", edge: "border-l-stock-in", text: "text-stock-in" },
];

const URGENCY_STYLE = {
  blocked: "bg-rose-50 text-stock-out border-rose-200",
  problem: "bg-gold-light text-gold border-amber-200",
  question: "bg-slate-50 text-muted2 border-slate-200",
};

function ago(value) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return today ? time : `${formatDate(value)}, ${time}`;
}

/** Waiting on you means they spoke last — not merely that it's unresolved. */
// A path means little at a glance; the page name is what you'd recognise.
const PAGE_NAMES = {
  "/overview": "Overview",
  "/catalog": "Catalog",
  "/releases": "Releases",
  "/production": "Production",
  "/registrations": "Registrations",
  "/writers": "Writers",
  "/import": "Import & export",
  "/settings": "Settings",
  "/team": "Team",
  "/support": "Support",
};

function pageName(path) {
  if (!path) return "";
  return PAGE_NAMES[path] || path;
}

function needsReply(t) {
  const last = t.messages?.[t.messages.length - 1];
  return t.status !== "resolved" && last && !last.from_support;
}

// Owner-console page, not scoped to any one client business — "owner" is a
// fixed cache scope rather than a tenantId.
const CK = cacheKey("owner-support", "owner");

export default function Support() {
  const confirm = useConfirm();
  const cached = cacheGet(CK);
  // Seeded from cache so revisiting (even after a reload) shows the queue
  // instantly instead of flashing a loading skeleton — see lib/cache.js.
  const [tickets, setTickets] = useState(() => cached || []);
  const [loading, setLoading] = useState(() => !cached);
  const [filter, setFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  // Only show a loading skeleton once a fetch has actually taken a moment —
  // a cached/fast load resolves before the delay and never flashes one.
  const showLoading = useDelayedLoading(loading);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);
  const first = useRef(true);

  const load = useCallback(async (selectId) => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data);
      cacheSet(CK, data);
      setActiveId((current) => selectId || current || data[0]?.ticket_id || null);
      await api.post("/support/seen").catch(() => {});
      // Tell the sidebar badge so it doesn't wait for its own poll.
      emitChange();
    } catch (err) {
      if (first.current) toast.error(apiError(err));
    } finally {
      first.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Quiet background refresh — no spinner, no lost scroll position.
    const timer = setInterval(() => load(), 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const active = tickets.find((t) => t.ticket_id === activeId) || null;

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [active?.messages?.length, activeId]);

  const counts = {
    all: tickets.length,
    open: tickets.filter((t) => t.status === "open").length,
    working: tickets.filter((t) => t.status === "working").length,
    resolved: tickets.filter((t) => t.status === "resolved").length,
  };
  const waiting = tickets.filter(needsReply).length;
  const shown = tickets.filter((t) => (filter === "all" ? true : t.status === filter));

  const patch = async (body) => {
    if (!active) return;
    setBusy(true);
    try {
      await api.patch(`/support/tickets/${active.ticket_id}`, body);
      await load(active.ticket_id);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    setBusy(true);
    try {
      await api.post(`/support/tickets/${active.ticket_id}/messages`, { body: reply });
      setReply("");
      await load(active.ticket_id);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!active) return;
    const ok = await confirm({
      title: "Delete this ticket?",
      body: "The whole conversation goes with it. This can't be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/support/tickets/${active.ticket_id}`);
      toast.success("Ticket deleted");
      setActiveId(null);
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const idx = active ? STAGES.findIndex((s) => s.key === active.status) : -1;
  const prev = STAGES[idx - 1];
  const next = STAGES[idx + 1];

  if (loading) {
    return (
      <Layout owner title="Support">{showLoading && <LoadingPanel rows={4} />}</Layout>
    );
  }

  return (
    <Layout owner
      title="Support"
      subtitle={
        waiting
          ? `${waiting} needs a reply`
          : counts.all
          ? "Nothing waiting on you"
          : "No tickets yet"
      }
      action={
        tickets.length > 0 && (
          <div className="flex gap-0.5 bg-card border border-line rounded-lg p-0.5">
            {[
              { key: "all", label: "All", n: counts.all },
              { key: "open", label: "New", n: counts.open },
              { key: "working", label: "Working", n: counts.working },
              { key: "resolved", label: "Sorted", n: counts.resolved },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  "px-2.5 py-1.5 rounded-md text-[12.5px] font-medium transition-colors whitespace-nowrap",
                  filter === f.key ? "bg-brand-light text-brand" : "text-muted2 hover:text-ink"
                )}
              >
                {f.label} <span className="font-mono text-[11px]">{f.n}</span>
              </button>
            ))}
          </div>
        )
      }
    >
      {tickets.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={LifeBuoy}
            title="No tickets yet"
            body="When a client reports a problem it lands here, with the page they were on and who they are."
          />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)] gap-4 items-start">
          {/* Each ticket is its own rounded card rather than a divided row. */}
          <div className="space-y-2 lg:max-h-[calc(100vh-210px)] lg:overflow-y-auto lg:pr-1">
            {shown.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line py-10 text-center text-[12.5px] text-muted2">
                Nothing here.
              </div>
            ) : (
              shown.map((t) => {
                const stage = STAGES.find((s) => s.key === t.status) || STAGES[0];
                const isActive = t.ticket_id === activeId;
                return (
                  <button
                    key={t.ticket_id}
                    onClick={() => setActiveId(t.ticket_id)}
                    className={cn(
                      "w-full text-left rounded-xl border border-l-[3px] bg-card px-3.5 py-3 transition-colors",
                      stage.edge,
                      isActive
                        ? "border-brand bg-brand-light/60"
                        : "border-line hover:border-brand-ring"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {/* Status reads from the card edge and the label colour —
                          a dot is too small to scan a list by. */}
                      <span
                        className={cn(
                          "text-[11px] uppercase tracking-[0.06em] font-medium",
                          stage.text
                        )}
                      >
                        {stage.title}
                      </span>
                      {needsReply(t) ? (
                        <span className="ml-auto bg-brand text-white text-[10px] rounded-full px-2 py-0.5">
                          reply
                        </span>
                      ) : (
                        <span className="ml-auto text-[11px] text-muted2">{ago(t.last_at)}</span>
                      )}
                    </div>
                    <div className="text-[13px] font-medium text-ink mt-1.5 truncate">
                      {t.tenant_name || "No client"}
                    </div>
                    <div className="text-[12.5px] text-muted2 mt-0.5 truncate">
                      {t.messages?.[t.messages.length - 1]?.body || ""}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {active && (
            <div className="panel overflow-hidden flex flex-col">
              <div className="px-5 py-3.5 border-b border-line">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11.5px]",
                      URGENCY_STYLE[active.urgency]
                    )}
                  >
                    {active.urgency_label}
                  </span>
                  <span className="text-[13px] font-medium text-ink">
                    {active.tenant_name || "No client"}
                  </span>
                  {(() => {
                    const st = STAGES.find((x) => x.key === active.status) || STAGES[0];
                    return (
                      <span
                        className={cn(
                          "ml-auto text-[11px] uppercase tracking-[0.06em] font-medium",
                          st.text
                        )}
                      >
                        {st.title}
                      </span>
                    );
                  })()}
                  {active.tenant_deleted && (
                    <span className="text-[11.5px] text-muted2">(client deleted)</span>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-2 text-[11.5px] text-muted2 flex-wrap">
                  <span>{active.user_name}</span>
                  {active.page && (
                    <span className="inline-flex items-center gap-1.5">
                      <FileText className="w-3 h-3" />
                      {pageName(active.page)}
                    </span>
                  )}
                  {active.user_email && (
                    <a
                      href={`mailto:${active.user_email}?subject=Re: your message`}
                      className="inline-flex items-center gap-1 link-quiet"
                    >
                      <Mail className="w-3 h-3" />
                      {active.user_email}
                    </a>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-3">
                  {prev && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => patch({ status: prev.key })}
                      className="h-8 px-2 text-muted2"
                      title={`Move back to ${prev.title}`}
                      aria-label={`Move back to ${prev.title}`}
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {next ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => patch({ status: next.key })}
                      className="h-8 px-3 text-[12.5px]"
                    >
                      {next.key === "resolved" ? "Mark sorted" : next.title}
                      <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-stock-in">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Sorted — they can't reply, you still can
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={remove}
                    className="h-8 px-2 ml-auto text-muted2 hover:text-stock-out"
                    aria-label="Delete ticket"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              <div
                ref={threadRef}
                className="flex-1 max-h-[400px] overflow-y-auto px-5 py-4 space-y-4"
              >
                {active.messages?.map((m) => (
                  <div
                    key={m.message_id}
                    className={cn(
                      "max-w-[78%]",
                      m.from_support
                        ? "ml-auto border-r-2 border-brand pr-3 text-right"
                        : "mr-auto border-l-2 border-line pl-3"
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-baseline gap-2",
                        m.from_support && "justify-end"
                      )}
                    >
                      <span
                        className={cn(
                          "text-[12.5px] font-medium",
                          m.from_support ? "text-brand" : "text-ink"
                        )}
                      >
                        {m.from_support ? "You" : m.author_name}
                      </span>
                      <span className="text-[11px] text-muted2">{when(m.at)}</span>
                    </div>
                    <div className="text-[13.5px] text-ink leading-relaxed whitespace-pre-wrap mt-1">
                      {m.body}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-line px-5 py-3.5 flex gap-2">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Write a reply…"
                  className="flex-1"
                />
                <Button
                  onClick={send}
                  disabled={busy || !reply.trim()}
                  className="bg-brand hover:bg-brand-hover shrink-0"
                  aria-label="Send reply"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
